/**
 * BookingAnswersRows — overview read renderer for the answers-snapshot json
 * properties (`ahoy_custom_answers` on a booking, `ahoy_booking_answers` on a
 * meeting), wired through `customPropertyReadRenderers`.
 *
 * Instead of one foreign block, the snapshot is decomposed into native rows —
 * one `OverviewFieldRow` per question, with the type icon, label column, and
 * value renderer every other property in the rail uses (the same treatment
 * `JsonPropertyReadFields` gives JSON-with-schema properties). A micro group
 * label names the group so a self-reported "Company size" can't be mistaken
 * for the CRM-derived one that `MeetingOverviewEnrichment` renders beside it.
 *
 * Renders nothing when the snapshot holds no answers, so the rail omits the
 * group rather than showing an empty heading.
 */

import { useMemo } from 'react';

import { ExpandableMultiValueLabel } from '@/lib/crm/components/properties/labels/ExpandableMultiValueLabel';
import { ExpandablePropertyValue } from '@/lib/crm/components/properties/labels/ExpandablePropertyValue';
import type { BookingAnswer } from '@/lib/crm/utils/bookingQuestions';
import {
  buildBookingAnswerPropertyType,
  getBookingAnswerDisplayValue,
  parseBookingAnswers,
} from '@/lib/crm/utils/bookingQuestions';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import { OverviewFieldRow } from '../components/OverviewFieldRow';
import { OverviewGroupLabel } from '../components/OverviewGroupLabel';

interface BookingAnswersRowsProps {
  value: unknown;
  orgId: number;
}

interface BookingAnswerRowProps {
  answer: BookingAnswer;
  orgId: number;
}

// Same value typography as the rail's native property rows (overview-section).
const VALUE_CLASSES = 'text-xs md:text-sm font-medium text-fg-primary flex-1';

function BookingAnswerRow({ answer, orgId }: BookingAnswerRowProps) {
  const propertyType = useMemo(
    () => buildBookingAnswerPropertyType(answer),
    [answer]
  );
  const displayValue = useMemo(
    () => getBookingAnswerDisplayValue(answer),
    [answer]
  );
  const Icon = getPropertyTypeIconComponent(propertyType);

  return (
    <OverviewFieldRow
      icon={Icon}
      label={answer.label}
      valueClassName={VALUE_CLASSES}
    >
      {/* Long free-text answers clamp with "Show more"; multi-select answers
          collapse to "first +N" exactly like a native multi-select row. */}
      <ExpandablePropertyValue
        value={displayValue}
        propertyType={propertyType}
        orgId={orgId}
        ValueComponent={ExpandableMultiValueLabel}
      />
    </OverviewFieldRow>
  );
}

export function BookingAnswersRows({ value, orgId }: BookingAnswersRowsProps) {
  const answers = useMemo(() => parseBookingAnswers(value), [value]);

  if (answers.length === 0) {
    return null;
  }

  return (
    <>
      {/* `pt-1` adds a breath above the label so it reads as the start of a
          group rather than a caption for the row above it. */}
      <OverviewGroupLabel className='pt-1'>
        Booking questions
      </OverviewGroupLabel>
      {answers.map(answer => (
        <BookingAnswerRow key={answer.key} answer={answer} orgId={orgId} />
      ))}
    </>
  );
}
